import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import CalendarIcon from 'icons/Calendar.icon';
import { Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import {
  DatepickerProps,
  DatepickerRef,
} from 'ncoded-component-library/build/components/organisms/Datepicker/models';
// The library's `Datepicker` export is just this provider wrapped around a body
// we can't configure, so we pair it with our own body instead.
import DatepickerProvider from 'ncoded-component-library/build/components/organisms/Datepicker/provider';
import { FieldRenderProps } from 'react-final-form';
import DatepickerCalendar from './components/DatepickerCalendar';
import { formatDate } from './utils';
import FieldWrapper from 'components/FieldWrapper';
import { useTranslation } from 'react-i18next';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './DatepickerField.styles.scss';

const TRIGGER_ID_KEYWORD = 'dp-trigger';

type DatepickerFieldProps = FieldWrapperProps &
  DatepickerProps &
  FieldRenderProps<string, HTMLElement> & {
    disabled?: boolean;
    customOnChange?: (date: Date, isValid: boolean) => void;
  };

const DatepickerField: React.ForwardRefRenderFunction<
  DatepickerRef,
  DatepickerFieldProps
> = (props, ref) => {
  const {
    className,
    input: { value, onChange: fOnChange, name },
    meta: { touched, error },
    disabled,
    customOnChange,
    ...rest
  } = props;
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const hasError = useMemo(() => touched && error, [error, touched]);

  const { t } = useTranslation();

  const handleChange = useCallback(
    (date: Date) => {
      fOnChange(date);
      customOnChange?.(date, !hasError);
      dropdown.setIsOpen(false);
    },
    [hasError, dropdown, customOnChange, fOnChange],
  );

  useEffect(() => {
    disabled && fOnChange(undefined);
  }, [disabled, fOnChange]);

  const classes = classNames('datepicker-field', className);

  // The library's year and month grids render bare `<button>`s with no `type`,
  // which inside a form means `type="submit"` — so picking a year submits the
  // form under the picker and fires native validation. The day grid sets
  // `type="button"` correctly, so this only bites once you drill into the
  // month/year views. Cancelling the click's default action leaves React's own
  // handler to run, so the year is still selected.
  const preventImplicitSubmit = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const button = (event.target as HTMLElement).closest('button');

      if (button?.type === 'submit') event.preventDefault();
    },
    [],
  );

  useEffect(() => {
    const clickcb = (ev: any) => {
      const triggerId = (ev as any).target.id;

      if (
        triggerId &&
        triggerId.includes(TRIGGER_ID_KEYWORD) &&
        triggerId !== `${name}-${TRIGGER_ID_KEYWORD}`
      )
        dropdown.setIsOpen(false);
    };
    document.addEventListener('click', clickcb);
    return () => {
      document.removeEventListener('click', clickcb);
    };
  }, [dropdown, name]);

  return (
    <FieldWrapper
      className={classes}
      error={error}
      hasError={hasError}
      name={name}
      {...rest}
    >
      <Dropdown
        className={classNames('datepicker-field__dropdown', {
          'datepicker-field__dropdown--error': hasError,
        })}
        ref={dropdownRef}
        onClickCapture={preventImplicitSubmit}
        trigger={
          <button
            type="button"
            className="datepicker-field__button"
            disabled={disabled}
            id={`${name}-${TRIGGER_ID_KEYWORD}`}
          >
            {value ? formatDate(value) : <p>{t('selectDate')}</p>}
            <CalendarIcon />
          </button>
        }
      >
        <DatepickerProvider
          value={value ? new Date(value) : new Date()}
          onChange={handleChange}
          {...rest}
          ref={ref}
        >
          <DatepickerCalendar />
        </DatepickerProvider>
      </Dropdown>
    </FieldWrapper>
  );
};
export default forwardRef(DatepickerField);
